import React, { useState } from 'react';
import { Container, Row, Col, Card, Form, Button, Alert, Spinner } from 'react-bootstrap';
import { passService, VerifyPassResponse } from '../services/services/passService';
import { useConfirmation } from '../contexts/ConfirmationContext';
import './VerifyPassPage.scss';

const VerifyPassPage: React.FC = () => {
  const { alert } = useConfirmation();
  
  const [loading, setLoading] = useState(false);
  const [verified, setVerified] = useState(false);
  const [passData, setPassData] = useState<VerifyPassResponse['pass'] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    identifier: '',
    email: '',
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setVerified(false);
    setPassData(null);

    try {
      const result = await passService.verifyPass(formData.identifier, formData.email);
      
      if (result.success && result.verified && result.pass) {
        setVerified(true);
        setPassData(result.pass);
      } else {
        setError(result.error || 'Verification failed');
      }
    } catch (err: any) {
      console.error('Error verifying pass:', err);
      setError(err.response?.data?.error || 'Failed to verify pass. Please check your details and try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setVerified(false);
    setPassData(null);
    setError(null);
    setFormData({ identifier: '', email: '' });
  };

  return (
    <div className="verify-pass-page" style={{ marginTop: 120, padding: '2rem 0' }}>
      <Container>
        <Row className="justify-content-center">
          <Col lg={8}>
            <div className="mb-5">
              <div className="verify-icon-wrapper">
                <div className="verify-icon">
                  <i className="fas fa-check-circle"></i>
                </div>
              </div>
              <h1 className="fw-bold mb-3">Verify Your Pass</h1>
              <p className="lead text-muted">Authenticate your ticket and view pass details</p>
            </div>

            <Card className="verify-card">
              <Card.Body>
                {error && (
                  <Alert variant="danger" className="mb-4">
                    <div className="d-flex align-items-center">
                      <i className="fas fa-exclamation-triangle me-2"></i>
                      <div>
                        <strong>Verification Failed</strong>
                        <p className="mb-0">{error}</p>
                      </div>
                    </div>
                  </Alert>
                )}

                {!verified ? (
                  <>
                    <div className="security-info mb-4">
                      <div className="security-header mb-3">
                        <div className="security-icon me-3">
                          <i className="fas fa-shield-alt"></i>
                        </div>
                        <div>
                          <h4>Important Security Information</h4>
                          <p className="text-muted mb-0">Keep your pass details secure and confidential</p>
                        </div>
                      </div>
                      <Row>
                        <Col md={6}>
                          <div className="security-point">
                            <i className="fas fa-lock me-2"></i>
                            <span>Keep your pass identifier confidential</span>
                          </div>
                        </Col>
                        <Col md={6}>
                          <div className="security-point">
                            <i className="fas fa-user-shield me-2"></i>
                            <span>Don't share pass details with unauthorized individuals</span>
                          </div>
                        </Col>
                        <Col md={6}>
                          <div className="security-point">
                            <i className="fas fa-exclamation-triangle me-2"></i>
                            <span>Report any suspicious activity immediately</span>
                          </div>
                        </Col>
                        <Col md={6}>
                          <div className="security-point">
                            <i className="fas fa-id-card me-2"></i>
                            <span>Present your pass with a valid ID at the venue</span>
                          </div>
                        </Col>
                      </Row>
                    </div>

                    <Form onSubmit={handleSubmit}>
                      <Form.Group className="mb-3">
                        <Form.Label>
                          <i className="fas fa-fingerprint me-2"></i>
                          Pass Identifier
                        </Form.Label>
                        <Form.Control
                          type="text"
                          required
                          value={formData.identifier}
                          onChange={(e) => setFormData({ ...formData, identifier: e.target.value })}
                          placeholder="Enter your unique pass identifier"
                        />
                        <Form.Text className="text-muted">The unique identifier shown on your ticket</Form.Text>
                      </Form.Group>

                      <Form.Group className="mb-4">
                        <Form.Label>
                          <i className="fas fa-envelope me-2"></i>
                          Email Address
                        </Form.Label>
                        <Form.Control
                          type="email"
                          required
                          value={formData.email}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          placeholder="Enter your registered email"
                        />
                        <Form.Text className="text-muted">Email address used during registration</Form.Text>
                      </Form.Group>

                      <Button
                        variant="primary"
                        type="submit"
                        className="w-100"
                        disabled={loading}
                      >
                        {loading ? (
                          <>
                            <Spinner animation="border" size="sm" className="me-2" />
                            Verifying...
                          </>
                        ) : (
                          <>
                            <i className="fas fa-check-circle me-2"></i>
                            Verify Pass
                          </>
                        )}
                      </Button>
                    </Form>
                  </>
                ) : (
                  <div className="success-state">
                    <div className="success-header mb-4">
                      <div className="success-icon me-3">
                        <i className="fas fa-check-circle"></i>
                      </div>
                      <div>
                        <h3>Pass Verified Successfully!</h3>
                        <p className="text-muted mb-0">This pass is valid and ready for use</p>
                      </div>
                    </div>

                    {passData?.photo_url && (
                      <div className="pass-photo text-center mb-4">
                        <img src={passData.photo_url} alt="Pass Photo" className="img-fluid" style={{ maxHeight: '200px' }} />
                      </div>
                    )}

                    <div className="pass-details mb-4">
                      <h4 className="mb-3">Pass Information</h4>
                      <Row>
                        <Col md={6}>
                          <div className="detail-item mb-3">
                            <div className="detail-label">
                              <i className="fas fa-calendar-alt me-2"></i>
                              Event
                            </div>
                            <div className="detail-value">{passData?.event.title}</div>
                          </div>
                        </Col>
                        <Col md={6}>
                          <div className="detail-item mb-3">
                            <div className="detail-label">
                              <i className="fas fa-tag me-2"></i>
                              Category
                            </div>
                            <div className="detail-value">{passData?.category.name}</div>
                          </div>
                        </Col>
                        <Col md={6}>
                          <div className="detail-item mb-3">
                            <div className="detail-label">
                              <i className="fas fa-user me-2"></i>
                              Name
                            </div>
                            <div className="detail-value">{passData?.name}</div>
                          </div>
                        </Col>
                        <Col md={6}>
                          <div className="detail-item mb-3">
                            <div className="detail-label">
                              <i className="fas fa-envelope me-2"></i>
                              Email
                            </div>
                            <div className="detail-value">{passData?.email}</div>
                          </div>
                        </Col>
                        {passData?.phone && (
                          <Col md={6}>
                            <div className="detail-item mb-3">
                              <div className="detail-label">
                                <i className="fas fa-phone me-2"></i>
                                Phone
                              </div>
                              <div className="detail-value">{passData.phone}</div>
                            </div>
                          </Col>
                        )}
                        {passData?.seat_info && (
                          <Col md={6}>
                            <div className="detail-item mb-3">
                              <div className="detail-label">
                                <i className="fas fa-chair me-2"></i>
                                Seat Information
                              </div>
                              <div className="seat-details">
                                <div className="seat-item">
                                  <span className="seat-label">Row:</span>
                                  <span className="seat-value">{passData.seat_info.row}</span>
                                </div>
                                <div className="seat-item">
                                  <span className="seat-label">Column:</span>
                                  <span className="seat-value">{passData.seat_info.column}</span>
                                </div>
                                <div className="seat-item">
                                  <span className="seat-label">Seat:</span>
                                  <span className="seat-value">{passData.seat_info.number}</span>
                                </div>
                              </div>
                            </div>
                          </Col>
                        )}
                        {passData?.additional_fields && Object.entries(passData.additional_fields).map(([label, value]) => (
                          <Col md={6} key={label}>
                            <div className="detail-item mb-3">
                              <div className="detail-label">
                                <i className="fas fa-info me-2"></i>
                                {label}
                              </div>
                              <div className="detail-value">{value}</div>
                            </div>
                          </Col>
                        ))}
                      </Row>
                    </div>

                    <Alert variant="warning" className="mb-4">
                      <div className="d-flex align-items-start">
                        <i className="fas fa-shield-alt me-2 mt-1"></i>
                        <div>
                          <strong>Security Reminder</strong>
                          <p className="mb-0">Please keep this information secure and present your pass along with a valid ID at the venue.</p>
                        </div>
                      </div>
                    </Alert>

                    <div className="text-center">
                      <Button variant="outline-primary" onClick={handleReset}>
                        <i className="fas fa-redo me-2"></i>
                        Verify Another Pass
                      </Button>
                    </div>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default VerifyPassPage;

